import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { apiErrorMessage } from "@/lib/apiError";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Trash2, Eye, Quote, Target, BarChart3, Image as ImageIcon } from "lucide-react";

/** Iter 170 · Vision Board — goals, quotes, metrics, and images that keep
 * the mission in front of the founder. */

const KINDS = [
  { key: "goal", label: "Goal", Icon: Target },
  { key: "quote", label: "Quote", Icon: Quote },
  { key: "metric", label: "Metric", Icon: BarChart3 },
  { key: "image", label: "Image", Icon: ImageIcon },
];
const PALETTE = ["#0a0a0f", "#f97316", "#0ea5e9", "#10b981", "#8b5cf6", "#e11d48"];
const BLANK = { kind: "goal", title: "", body: "", image_url: "", color: "#0a0a0f" };

export default function VisionBoardTab({ wsId }) {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get(`/workspaces/${wsId}/bizdev/vision`)
      .then((r) => setRows(r.data.rows || []))
      .catch((e) => toast.error(apiErrorMessage(e, "Failed to load vision board")));
  }, [wsId]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!editing.title?.trim()) { toast.error("Title is required"); return; }
    setBusy(true);
    const body = { kind: editing.kind, title: editing.title, body: editing.body || "",
                     image_url: editing.image_url || null, color: editing.color };
    try {
      if (editing.id) await api.patch(`/workspaces/${wsId}/bizdev/vision/${editing.id}`, body);
      else await api.post(`/workspaces/${wsId}/bizdev/vision`, body);
      toast.success("Vision board updated");
      setEditing(null);
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
    finally { setBusy(false); }
  };

  const remove = async (v) => {
    if (!window.confirm("Remove this from the vision board?")) return;
    try {
      await api.delete(`/workspaces/${wsId}/bizdev/vision/${v.id}`);
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "Delete failed")); }
  };

  return (
    <div className="space-y-3" data-testid="bizdev-vision-tab">
      <div className="flex items-center">
        <div className="text-xs text-muted-foreground">
          The wall you look at every morning — north-star goals, numbers, and words that keep you moving.
        </div>
        <div className="ml-auto">
          <Button size="sm" className="h-8 text-xs" onClick={() => setEditing({ ...BLANK })}
                  data-testid="vision-new-btn">
            <Plus className="w-3 h-3 mr-1" /> Add to board
          </Button>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
        {rows.map((v) => (
          <div key={v.id} className="group relative border border-border overflow-hidden"
               style={{ background: v.kind === "quote" ? "#fff" : v.color,
                         color: v.kind === "quote" ? "#111" : "#fff" }}
               data-testid={`vision-card-${v.id}`}>
            {v.kind === "image" && v.image_url && (
              <img src={v.image_url} alt={v.title}
                   className="w-full h-36 object-cover" />
            )}
            <div className="p-4">
              {v.kind === "quote" ? (
                <>
                  <div className="text-lg leading-snug font-serif italic">“{v.title}”</div>
                  {v.body && <div className="text-xs text-muted-foreground mt-2">— {v.body}</div>}
                </>
              ) : v.kind === "metric" ? (
                <>
                  <div className="text-[10px] uppercase tracking-widest opacity-70">{v.body || "Target"}</div>
                  <div className="text-3xl font-bold mt-1">{v.title}</div>
                </>
              ) : (
                <>
                  <div className="text-[10px] uppercase tracking-widest opacity-70">
                    {KINDS.find((k) => k.key === v.kind)?.label}
                  </div>
                  <div className="text-base font-semibold mt-1 leading-snug">{v.title}</div>
                  {v.body && <div className="text-xs mt-1.5 opacity-80">{v.body}</div>}
                </>
              )}
            </div>
            <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
              <button type="button" onClick={() => setEditing({ ...v })}
                      className="bg-black/50 text-white p-1 rounded"
                      data-testid={`vision-edit-${v.id}`}>
                <Eye className="w-3 h-3" />
              </button>
              <button type="button" onClick={() => remove(v)}
                      className="bg-black/50 text-white p-1 rounded"
                      data-testid={`vision-delete-${v.id}`}>
                <Trash2 className="w-3 h-3" />
              </button>
            </div>
          </div>
        ))}
        {rows.length === 0 && (
          <div className="col-span-full border border-dashed border-border p-12 text-center text-sm text-muted-foreground">
            <Eye className="w-6 h-6 mx-auto mb-2" />
            Empty board. Add the revenue number, the mission line, the quote that fires you up.
          </div>
        )}
      </div>

      {editing && (
        <Dialog open onOpenChange={(o) => { if (!o) setEditing(null); }}>
          <DialogContent className="max-w-md" data-testid="vision-dialog">
            <DialogHeader>
              <DialogTitle>{editing.id ? "Edit board item" : "Add to vision board"}</DialogTitle>
            </DialogHeader>
            <div className="space-y-2.5 text-sm">
              <div>
                <label className="text-[10px] text-muted-foreground uppercase tracking-widest">Type</label>
                <Select value={editing.kind} onValueChange={(v) => setEditing({ ...editing, kind: v })}>
                  <SelectTrigger className="h-8 text-xs mt-0.5" data-testid="vision-kind-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {KINDS.map((k) => (
                      <SelectItem key={k.key} value={k.key}>{k.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Input value={editing.title}
                     onChange={(e) => setEditing({ ...editing, title: e.target.value })}
                     placeholder={editing.kind === "metric" ? "₹1 Cr ARR" : editing.kind === "quote" ? "The quote itself" : "Title"}
                     data-testid="vision-title-input" />
              <Input value={editing.body || ""}
                     onChange={(e) => setEditing({ ...editing, body: e.target.value })}
                     placeholder={editing.kind === "quote" ? "Attribution" : editing.kind === "metric" ? "Label (e.g. by Dec 2026)" : "Detail (optional)"}
                     className="h-8 text-xs" data-testid="vision-body-input" />
              {editing.kind === "image" && (
                <Input value={editing.image_url || ""}
                       onChange={(e) => setEditing({ ...editing, image_url: e.target.value })}
                       placeholder="Image URL"
                       className="h-8 text-xs" data-testid="vision-image-input" />
              )}
              <div>
                <label className="text-[10px] text-muted-foreground uppercase tracking-widest">Color</label>
                <div className="flex gap-1.5 mt-1">
                  {PALETTE.map((c) => (
                    <button key={c} type="button"
                            onClick={() => setEditing({ ...editing, color: c })}
                            className={`w-6 h-6 rounded-full border-2 ${editing.color === c ? "border-foreground scale-110" : "border-transparent"}`}
                            style={{ background: c }} />
                  ))}
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" size="sm" onClick={() => setEditing(null)}>Cancel</Button>
              <Button size="sm" onClick={save} disabled={busy} data-testid="vision-save-btn">
                {busy ? "Saving…" : "Save"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
